<template>
  <Layout>
    <Head :title="t('help.title') + ' - Kiosk'" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="space-y-8">
        <!-- Header -->
        <div class="border-b pb-4">
          <h1 class="text-3xl font-bold text-gray-900">{{ t('help.get_help') }}</h1>
          <p class="text-gray-600 mt-2">{{ t('help.support_description') }}</p>
        </div>

        <!-- Call Center Card -->
        <Card class="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle class="flex items-center gap-3">
              <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              {{ t('help.call_center_title') }}
            </CardTitle>
            <CardDescription>
              {{ t('help.call_center_description') }}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div class="flex items-center justify-between bg-white rounded-lg p-4 border">
              <div class="flex items-center gap-4">
                <div class="bg-blue-100 p-3 rounded-full">
                  <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p class="text-2xl font-bold text-gray-900">1-888-274-7292</p>
                  <p class="text-sm text-gray-600">{{ t('help.toll_free') }}</p>
                </div>
              </div>
              <Button 
                @click="callNumber"
                class="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {{ t('help.call_now') }}
              </Button>
            </div>
          </CardContent>
        </Card>

        <!-- Hours Card -->
        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-3">
              <svg class="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ t('help.support_hours') }}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="font-medium">{{ t('help.monday_friday') }}</span>
                <span>{{ t('help.business_hours') }}</span>
              </div>
              <div class="flex justify-between text-gray-600">
                <span>{{ t('help.saturday_sunday') }}</span>
                <span>{{ t('help.closed') }}</span>
              </div>
            </div>
            <div class="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p class="text-sm text-amber-800">
                <strong>{{ t('help.note') }}:</strong> {{ t('help.emergency_note') }}
              </p>
            </div>
          </CardContent>
        </Card>

        <!-- Common Questions -->
        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-3">
              <svg class="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ t('help.common_questions') }}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div class="space-y-4">
              <div class="border-l-4 border-blue-200 pl-4">
                <h4 class="font-medium text-gray-900">{{ t('help.question_login') }}</h4>
                <p class="text-sm text-gray-600 mt-1">{{ t('help.answer_login') }}</p>
              </div>
              <div class="border-l-4 border-blue-200 pl-4">
                <h4 class="font-medium text-gray-900">{{ t('help.question_appointment') }}</h4>
                <p class="text-sm text-gray-600 mt-1">{{ t('help.answer_appointment') }}</p>
              </div>
              <div class="border-l-4 border-blue-200 pl-4">
                <h4 class="font-medium text-gray-900">{{ t('help.question_profile') }}</h4>
                <p class="text-sm text-gray-600 mt-1">{{ t('help.answer_profile') }}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </Layout>
</template>

<script setup>
import { Head } from '@inertiajs/vue3'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import Layout from '@/Layouts/Minimal.vue'
import { t } from '@/entrypoints/translations.js'

// Function to initiate phone call (works on mobile devices)
const callNumber = () => {
  window.location.href = 'tel:+18882747292'
}
</script>
